import * as React from "react";

import { AlertDialog, RobotoButton } from "@/shared/components";
import {
  useActiveInvocationCount,
  useActiveSessionInvocations,
} from "@/shared/domain/actions/hooks";
import { CancelIcon } from "@/shared/foundation/icons/mui";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { useCancelInvocationsByDataset } from "./useCancelInvocationsByDataset";
import { useCancelInvocationsBySession } from "./useCancelInvocationsBySession";

export interface CancelAllProps {
  datasetId?: string;
}

/**
 * A button to cancel all currently active invocations associated with a dataset.
 * Automatically polls for active invocations and only renders if there are active invocations to cancel.
 */
export function CancelAll({ datasetId }: CancelAllProps) {
  const cancelInvocationsMutation = useCancelInvocationsByDataset();
  const { data: activeCount = 0, isPending } =
    useActiveInvocationCount(datasetId);

  const handleCancel = React.useCallback(async () => {
    if (datasetId === undefined) {
      return;
    }

    try {
      await cancelInvocationsMutation.mutateAsync({ datasetId });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      ErrorMonitoringService.captureError(err, { datasetId });
    }
  }, [datasetId, cancelInvocationsMutation]);

  if (datasetId === undefined || isPending || activeCount === 0) {
    return null;
  }

  return (
    <CancelAllButton
      entity="dataset"
      eventProperties={{ datasetId }}
      pending={cancelInvocationsMutation.isPending}
      onConfirm={handleCancel}
    />
  );
}

export interface CancelAllOnSessionProps {
  /** Cancel invocations whose input explicitly names this session by ID. */
  sessionId: string;
}

/**
 * A button to cancel the active invocations whose input names a session. Renders only while the
 * session has some.
 */
export function CancelAllOnSession({ sessionId }: CancelAllOnSessionProps) {
  const cancelInvocationsMutation = useCancelInvocationsBySession();
  const { data, isPending } = useActiveSessionInvocations(sessionId);

  const handleCancel = React.useCallback(async () => {
    try {
      await cancelInvocationsMutation.mutateAsync({ sessionId });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      ErrorMonitoringService.captureError(err, { sessionId });
    }
  }, [sessionId, cancelInvocationsMutation]);

  if (isPending || (data?.items.length ?? 0) === 0) {
    return null;
  }

  return (
    <CancelAllButton
      entity="session"
      eventProperties={{ sessionId }}
      pending={cancelInvocationsMutation.isPending}
      onConfirm={handleCancel}
    />
  );
}

function CancelAllButton({
  entity,
  eventProperties,
  pending,
  onConfirm,
}: {
  entity: "dataset" | "session";
  eventProperties: { datasetId: string } | { sessionId: string };
  pending: boolean;
  onConfirm: () => Promise<void>;
}) {
  const [alertDialogOpen, setAlertDialogOpen] = React.useState(false);

  return (
    <>
      <RobotoButton
        eventName="CancelAssociatedInvocations"
        eventProperties={eventProperties}
        variant="outlined"
        color="warning"
        size="small"
        startIcon={<CancelIcon />}
        onClick={() => setAlertDialogOpen(true)}
        disabled={pending}
        loading={pending}
      >
        Cancel All
      </RobotoButton>

      <AlertDialog
        dialogOpen={alertDialogOpen}
        handleClose={() => setAlertDialogOpen(false)}
        dialogTitle="Cancel All Associated Invocations"
        dialogText={`Are you sure you want to cancel all active invocations associated with this ${entity}?`}
        dialogAction={onConfirm}
      />
    </>
  );
}
